import React, { useEffect, useState } from 'react'

import ChatService from '../../services/ChatService'
import UserService from '../../services/UserService'

import ChatRoom from './ChatRoom'

import './Chat.scss'

const ChatDrawer = ({ isOpen, onClose, currentUser }) => {
  const [threads, setThreads] = useState([])
  const [selectedThread, setSelectedThread] = useState(null)
  const [loading, setLoading] = useState(false)

  const [isCreating, setIsCreating] = useState(false)
  const [newSubject, setNewSubject] = useState('')
  const [allUsers, setAllUsers] = useState([])
  const [selectedRecipient, setSelectedRecipient] = useState('')

  useEffect(() => {
    if (isOpen) {
      loadThreads()
    }
  }, [isOpen])

  useEffect(() => {
    if (isCreating && allUsers.length === 0) {
      loadUsers()
    }
  }, [isCreating])

  const loadUsers = async () => {
    try {
      const users = await UserService.getAll()
      // Filter out current user
      setAllUsers(users.filter(u => u.id !== currentUser.id))
    } catch (err) {
      console.error('Failed to load users', err)
    }
  }

  const loadThreads = async () => {
    setLoading(true)
    try {
      const data = await ChatService.getThreads(1, 50)
      // Filter out case-related threads (they belong in CaseMessagesTab)
      const generalThreads = (Array.isArray(data) ? data : []).filter(t => !t.caseNumber)
      setThreads(generalThreads)
    } catch (err) {
      console.error('Failed to load threads', err)
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async e => {
    e.preventDefault()
    if (!newSubject.trim()) return
    if (!selectedRecipient) {
      alert('Please select a recipient')
      return
    }

    try {
      // Create general thread (caseNumber = null)
      const res = await ChatService.createThread(null, newSubject, [selectedRecipient])

      // Manually hydrate participants for UI immediate display
      const recipientUser = allUsers.find(u => u.id === selectedRecipient)
      if (recipientUser) {
        if (!res.participants) res.participants = []
        // Add current user (me)
        res.participants.push({ userId: currentUser.id, fullName: `${currentUser.firstName} ${currentUser.lastName}` })
        // Add recipient
        res.participants.push({
          userId: recipientUser.id,
          fullName: `${recipientUser.firstName} ${recipientUser.lastName}`,
        })
      }

      setThreads([res, ...threads])
      setIsCreating(false)
      setNewSubject('')
      setSelectedRecipient('')
      setSelectedThread(res)
    } catch (err) {
      console.error('Failed to create thread', err)
      alert('Error creating thread')
    }
  }

  const getRecipientName = thread => {
    if (!thread.participants || thread.participants.length === 0) return 'Unknown'
    // Find first participant that isn't me
    const other = thread.participants.find(p => p.userId !== currentUser.id)
    return other ? other.fullName : 'Me'
  }

  const getRecipientInitials = thread => {
    const name = getRecipientName(thread)
    if (name === 'Unknown' || name === 'Me') return '?'
    const parts = name.split(' ')
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`
    return name.substring(0, 2)
  }

  const handleMarkAllRead = async () => {
    // Optimistic update - update UI immediately
    window.dispatchEvent(new Event('chat:mark-all-read'))
    try {
      await ChatService.markAllRead()
    } catch (err) {
      console.error('Failed to mark all read', err)
      // Optional: Revert via another event if needed, but polling will fix it eventually
    }
  }

  // if (!isOpen) return null // Removed to allow animation

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div className={`chat-drawer-overlay ${isOpen ? 'visible' : ''}`} onClick={onClose} />
      <div className={`chat-drawer ${isOpen ? 'open' : ''}`}>
        {selectedThread ? (
          <ChatRoom thread={selectedThread} currentUser={currentUser} onBack={() => setSelectedThread(null)} />
        ) : (
          <>
            <div className="chat-drawer-header">
              <h5 className="mb-0">Messages</h5>
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-link btn-sm p-0 mr-3 text-muted"
                  onClick={() => handleMarkAllRead()}
                  style={{ textDecoration: 'none', fontSize: '0.9rem' }}
                >
                  Mark all read
                </button>
                <button
                  className="btn btn-sm btn-light me-2"
                  title="New Message"
                  onClick={() => setIsCreating(!isCreating)}
                >
                  <i className={`fas ${isCreating ? 'fa-times' : 'fa-edit'}`}></i>
                </button>
                <button className="btn btn-sm btn-close" onClick={onClose}></button>
              </div>
            </div>

            {isCreating && (
              <div className="p-3 bg-light border-bottom">
                <form onSubmit={handleCreate}>
                  <div className="form-group mb-2">
                    <select
                      className="form-control"
                      value={selectedRecipient}
                      onChange={e => setSelectedRecipient(e.target.value)}
                      autoFocus // eslint-disable-line jsx-a11y/no-autofocus
                    >
                      <option value="">Select Recipient...</option>
                      {allUsers.map(u => (
                        <option key={u.id} value={u.id}>
                          {u.firstName} {u.lastName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control mr-2"
                      placeholder="Subject..."
                      value={newSubject}
                      onChange={e => setNewSubject(e.target.value)}
                    />
                    <button className="btn btn-success" type="submit">
                      Start
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="thread-list">
              {loading ? (
                <div className="text-center p-3">Loading...</div>
              ) : threads.length === 0 ? (
                <div className="text-center p-3 text-muted">No conversations yet.</div>
              ) : (
                threads.map(thread => (
                  // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                  <div
                    key={thread.id}
                    className="thread-item d-flex align-items-center mb-2 p-2 border-bottom"
                    onClick={() => setSelectedThread(thread)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="mr-3">
                      {/* Avatar Placeholder */}
                      <div
                        className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white"
                        style={{ width: '40px', height: '40px', fontSize: '1.2rem' }}
                      >
                        {getRecipientInitials(thread)}
                      </div>
                    </div>
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div className="d-flex justify-content-between align-items-baseline">
                        <h6 className="mb-0 text-truncate font-weight-bold" title={getRecipientName(thread)}>
                          {getRecipientName(thread)}
                        </h6>
                        <small className="text-muted ml-2">{new Date(thread.updatedOn).toLocaleDateString()}</small>
                      </div>
                      <div className="text-muted text-truncate" style={{ fontSize: '0.9rem' }}>
                        {thread.subject || 'No Subject'}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="border-top text-center py-2" style={{ flexShrink: 0 }}>
              {/* Plain anchor avoids Router dependency — ChatDrawer renders outside <Router> */}
              <a href="/messages" className="small text-primary" onClick={onClose}>
                See all messages →
              </a>
            </div>
          </>
        )}
      </div>
    </>
  )
}

export default ChatDrawer
